import { v4 as uuidv4 } from 'uuid'
import { DeliveryEnum } from '../interfaces'
import {
  ShipmentGroup,
  ShipmentPieceRow,
} from '../interfaces/Import/ShipmentGroupInterface'

/**
 * Construye el `shipment_list[]` (shape de POST /api/shipment/ y de la importación
 * asíncrona) desde los ShipmentGroup del Review.
 *
 * Extraído de ShipmentBulkUploadReview (deuda TODOS, eng-review E1) como función pura
 * para reusarlo en: el create flow, la validación pre-vuelo (#1) y el mapeo inverso
 * de corrección (Approach A). Una sola fuente evita drift entre code paths.
 *
 * NOTA (lossy): el valor declarado por pieza se colapsa aquí en un agregado
 * (declared_value.amount). El inverso payload→grupo no puede recuperar el valor por
 * pieza individual; ver el mapeo inverso para cómo se reconstruye.
 */

export interface BuildShipmentContext {
  user: any
  businessUnits: any[]
  citiesByBu: any[]
  identificationTypes: any[]
  allowedServices: any[]
  paymentMode: string
  serviceId?: number
}

export function buildShipmentList(
  gs: ShipmentGroup[],
  ctx: BuildShipmentContext
): { shipment_list: any[] } {
  const {
    user,
    businessUnits,
    citiesByBu,
    identificationTypes,
    allowedServices,
    paymentMode,
    serviceId,
  } = ctx
  const fallbackService = serviceId ?? allowedServices[0]?.serviceID ?? 60

  const shipment_list = gs.map((g) => {
    const isHome = g.deliveryType === DeliveryEnum.DELIVERY
    const originBu = user.client?.businessUnit
    const destBu = businessUnits.find((b) => b.buCode === g.storeCode)
    const cityBuCode = citiesByBu.find(
      (c) => c.locationCode === g.destinationCity
    )?.buCode
    const cityBu = isHome
      ? businessUnits.find((b) => b.buCode === cityBuCode)
      : undefined

    const shipperAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.shipperDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')
    const consigneeAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.consigneeDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')

    return {
      origin: {
        home_pickup: false,
        store: originBu?.buCode || '',
        location: originBu?.location?.locationCode || '',
        address: {
          name: null,
          line:
            user.client?.fiscalAddress || originBu?.location?.address || 'N/A',
          landmark: null,
          postal_code: originBu?.location?.postalCode || null,
        },
      },
      destination: {
        home_delivery: isHome,
        store: isHome ? cityBu?.buCode || '' : destBu?.buCode || g.storeCode,
        location: isHome
          ? g.destinationCity || cityBu?.location?.locationCode || ''
          : destBu?.location?.locationCode || '',
        ...(isHome && g.latitude != null && g.longitude != null
          ? {
              coordinates: {
                latitude: g.latitude,
                longitude: g.longitude,
              },
            }
          : {}),
        address: {
          name: null,
          line: g.destinationAddress || destBu?.location?.address || '',
          landmark: null,
          postal_code: g.postalCode || null,
        },
      },
      shipper: {
        identification: `${shipperAbbr}-${g.shipperDocNumber}`,
        name: g.shipperName,
        phone:
          (user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode || '') +
          ' ' +
          (user.client?.listAccountPhone?.[0]?.phoneNumber || ''),
        email: user.client?.listAccountEmail?.[0]?.email || undefined,
      },
      consignee: {
        identification: `${consigneeAbbr}-${g.consigneeDocNumber}`,
        name: g.consigneeName,
        phone: `${user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode || '+58'} ${g.consigneePhone}`,
        email: g.consigneeEmail || undefined,
      },
      billing: {
        service: g.serviceId ?? fallbackService,
        payment_mode: parseInt(paymentMode),
        account_code: user.client?.accountCode || '',
      },
      declared_value: {
        amount: g.pieces.reduce(
          (acc, p) => acc + (p.declaredValue || 0) * (p.quantity || 1),
          0
        ),
        currency: 'USD',
        invoice_number: null,
      },
      notes: g.notes || null,
      partner_shipment_id: g.trackingNumber || null,
      pct_discount: null,
      parcels: {
        dimension_unit: 'cm',
        weight_unit: 'kg',
        items: g.pieces.flatMap((p) =>
          Array.from({ length: p.quantity || 1 }, () => ({
            height: p.height || 1,
            width: p.width || 1,
            length: p.length || 1,
            weight: p.weight || 1,
            content_category: parseInt(p.categoryId) || 49,
            content_description: p.productName || null,
          }))
        ),
      },
    }
  })

  return { shipment_list }
}

// ---------------------------------------------------------------------------
// Inverso: payload_json (ShipmentInsertRequestDTO) → ShipmentGroup (Approach A)
// ---------------------------------------------------------------------------

export interface ReverseShipmentContext {
  identificationTypes: any[]
  isCOD?: boolean
}

/** "V-12345678" → { docType: taxIdentificationTypeId, docNumber }. */
function parseIdentification(
  identification: string | undefined,
  identificationTypes: any[]
): { docType: string; docNumber: string } {
  const raw = (identification || '').trim()
  const dash = raw.indexOf('-')
  if (dash < 0) return { docType: '', docNumber: raw }
  const prefix = raw.slice(0, dash).trim()
  const docNumber = raw.slice(dash + 1).trim()
  const docType =
    identificationTypes
      .find(
        (t) =>
          (t.abbreviationName || '').replace(/-+$/, '').toUpperCase() ===
          prefix.toUpperCase()
      )
      ?.taxIdentificationTypeId?.toString() ?? ''
  return { docType, docNumber }
}

/**
 * Reconstruye un ShipmentGroup desde el payload_json serializado de un detalle con
 * error, para reabrirlo en el editor (Review) y corregirlo (Approach A, eng-review E2).
 *
 * LOSSY (documentado en buildShipmentList): el forward expande las piezas por cantidad
 * y colapsa el valor declarado en un agregado. El inverso re-agrupa piezas idénticas y
 * distribuye el agregado uniformemente (declaredValue = amount / totalItems) para que el
 * total al re-enviar coincida; el valor declarado por pieza individual NO es recuperable.
 * `productCode` tampoco viaja en los items → queda vacío.
 */
export function payloadJsonToShipmentGroup(
  payloadJson: string,
  ctx: ReverseShipmentContext
): ShipmentGroup {
  const { identificationTypes, isCOD = false } = ctx
  const p = JSON.parse(payloadJson)

  const isHome = !!p?.destination?.home_delivery
  const shipper = parseIdentification(
    p?.shipper?.identification,
    identificationTypes
  )
  const consignee = parseIdentification(
    p?.consignee?.identification,
    identificationTypes
  )

  // Teléfono: el forward guardó `${countryCode} ${numero}`. Recuperamos lo de después
  // del primer espacio (los dígitos sin el código de país).
  const rawPhone = (p?.consignee?.phone || '').trim()
  const spaceIdx = rawPhone.indexOf(' ')
  const consigneePhone =
    spaceIdx >= 0 ? rawPhone.slice(spaceIdx + 1).replace(/\s+/g, '') : rawPhone

  // Re-agrupar items idénticos → piezas con cantidad.
  const items: any[] = Array.isArray(p?.parcels?.items) ? p.parcels.items : []
  const totalItems = items.length
  const amount = Number(p?.declared_value?.amount) || 0
  const perItemValue = totalItems > 0 ? amount / totalItems : 0

  const pieceMap = new Map<string, ShipmentPieceRow>()
  for (const it of items) {
    const key = [
      it?.height,
      it?.width,
      it?.length,
      it?.weight,
      it?.content_category,
      it?.content_description,
    ].join('|')
    const existing = pieceMap.get(key)
    if (existing) {
      existing.quantity += 1
    } else {
      pieceMap.set(key, {
        quantity: 1,
        weight: Number(it?.weight) || 0,
        height: Number(it?.height) || 0,
        width: Number(it?.width) || 0,
        length: Number(it?.length) || 0,
        declaredValue: perItemValue,
        productName: it?.content_description || '',
        productCode: '',
        categoryId:
          it?.content_category != null ? String(it.content_category) : '49',
      })
    }
  }

  return {
    id: uuidv4(),
    trackingNumber: p?.partner_shipment_id || '',
    deliveryType: isHome ? DeliveryEnum.DELIVERY : DeliveryEnum.OFFICE,
    destinationCity: p?.destination?.location || '',
    storeCode: p?.destination?.store || '',
    shipperDocType: shipper.docType,
    shipperDocNumber: shipper.docNumber,
    shipperName: p?.shipper?.name || '',
    consigneeDocType: consignee.docType,
    consigneeDocNumber: consignee.docNumber,
    consigneeName: p?.consignee?.name || '',
    consigneePhone,
    consigneeEmail: p?.consignee?.email || '',
    destinationAddress: p?.destination?.address?.line || '',
    postalCode: p?.destination?.address?.postal_code || '',
    notes: p?.notes || '',
    latitude: isHome ? (p?.destination?.coordinates?.latitude ?? null) : null,
    longitude: isHome ? (p?.destination?.coordinates?.longitude ?? null) : null,
    pieces: Array.from(pieceMap.values()),
    isShipperLocked: isCOD,
    isDuplicateInDB: false,
  }
}
